import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createIsomorphicFn } from "@tanstack/react-start";
import { lazy, Suspense } from "react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import { Footer } from "@/components/ui/footer";
import { ProblemSection } from "@/components/sections/ProblemSection";
import logo from "@/assets/mind-media-logo.svg";

const ContactSection = lazy(() =>
  import("@/components/sections/ContactSection").then((m) => ({ default: m.ContactSection })),
);

const setEdgeCache = createIsomorphicFn()
  .server(async () => {
    const { setHtmlEdgeCache } = await import("@/lib/edge-cache.server");
    setHtmlEdgeCache();
  })
  .client(() => {});

const TITLE = "The Problem — Why Web Design Is Broken | Mind Media";
const DESCRIPTION =
  "Most agencies charge $5,000–$8,000 for a templated website that doesn't convert. Here's what's broken in the web design industry — and what we built instead.";

export const Route = createFileRoute("/the-problem")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://mindmedia.co/the-problem" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://mindmedia.co/the-problem" }],
  }),
  loader: () => {
    setEdgeCache();
  },
  component: TheProblemPage,
});

function TheProblemPage() {
  const navigate = useNavigate();
  const goToMockup = () => navigate({ to: "/get-mockup" });

  return (
    <main className="min-h-screen bg-background">
      <div className="relative isolate overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-40 -bottom-40 z-0 h-[420px] w-[420px] rounded-full bg-hero-glow-warm opacity-35 blur-3xl sm:-left-56 sm:-bottom-56 sm:h-[720px] sm:w-[720px] sm:blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 z-0 h-[420px] w-[420px] rounded-full bg-hero-glow-cool opacity-40 blur-3xl sm:-right-56 sm:-top-56 sm:h-[720px] sm:w-[720px] sm:blur-[120px]"
        />
        <section className="relative z-20 pb-4 text-center sm:pb-6">
          <HeroNav
            logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
            navigation={[
              { label: "Work", href: "/#work" },
              { label: "About", href: "/#about" },
              { label: "Services", href: "/services" },
              { label: "Pricing", href: "/#pricing" },
              { label: "FAQ", href: "/#faq" },
              { label: "Contact", href: "#contact" },
            ]}
            ctaButton={{ label: "Book a Meeting", href: "#contact" }}
          />

          <div className="relative mx-auto max-w-7xl px-4 pt-10 sm:px-6 sm:pt-14">
            <h1 className="mx-auto max-w-3xl text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              The Problem
            </h1>
          </div>
        </section>
      </div>

      <ProblemSection onCtaClick={goToMockup} />

      <Suspense fallback={<div className="min-h-[400px]" />}>
        <ContactSection compact />
      </Suspense>

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/the-problem", label: "The Problem" },
          { href: "/#work", label: "Work" },
          { href: "/#pricing", label: "Pricing" },
          { href: "/#faq", label: "FAQ" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}
